import type { DragEvent, PropsWithChildren } from 'react';
import { useRouter } from 'next/navigation';
import {
  AssetDndContextProvider,
  DraggableAssetActivator,
  DroppableAssetActivator,
} from '@frameio/assets/operations';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { type GlobalDndNamespace } from '@frameio/shared/dnd';
import { getLink } from '@frameio/shared/routing';
import { consumeEvent } from '@frameio/utils';
import { useFolderDndNamespace, useTransfersDnd } from '../../contexts';

export type FolderDndNamespace = Extract<
  GlobalDndNamespace,
  'move-to-copy-to' | 'project-nav'
>;

type FolderDropZoneProps = PropsWithChildren<{
  id: string;
}>;

export const FolderDropZone = ({ id, children }: FolderDropZoneProps) => {
  const dndNamespace = useFolderDndNamespace();
  const router = useRouter();
  const projectId = useActiveProjectId();
  const { setHoveredId, onTransferDrop } = useTransfersDnd() ?? {};

  // only support drop to upload on the project nav's folder tree for now
  const transferBindings =
    dndNamespace === 'project-nav'
      ? {
          onDragLeave(event: DragEvent) {
            consumeEvent(event);
            setHoveredId?.(null);
          },
          onDragOver(event: DragEvent) {
            consumeEvent(event);
            setHoveredId?.(id);
          },
          onDrop(event: DragEvent) {
            consumeEvent(event);

            if (event.dataTransfer) {
              onTransferDrop?.(id, event.dataTransfer);
              router.push(
                getLink({
                  page: 'folder',
                  folderAssetId: id,
                  projectId,
                })
              );
            }

            setHoveredId?.(null);
          },
        }
      : {};

  return (
    <AssetDndContextProvider id={id ?? ''}>
      <DraggableAssetActivator>
        <DroppableAssetActivator>
          <div data-dz={true} {...transferBindings}>
            {children}
          </div>
        </DroppableAssetActivator>
      </DraggableAssetActivator>
    </AssetDndContextProvider>
  );
};
